import React from "react";
import { Sparkles, Tv, Contact, Users, ScanText, Calendar, Camera, Plus, Film } from "lucide-react";
import { cn } from "../utils";

interface Props {
  onNewProject: () => void;
  onNavigate: (view: "dashboard" | "crm" | "people" | "calendar" | "onset" | "gantt" | "finance") => void;
  displayName?: string | null;
  canViewPeopleHr?: boolean;
}

export function InternalWelcome({ onNewProject, onNavigate, displayName, canViewPeopleHr = false }: Props) {
  const firstName = displayName?.trim().split(/\s+/)[0] || "there";
  const appVersion = `v${__APP_VERSION__}`;
  const sectionHighlights = [
    {
      title: "Projects",
      description: "Create bids, manage key project details, and keep your active work organised in one place.",
      icon: Tv,
      color: "bg-[#e4a4a5]",
      targetView: "dashboard" as const,
    },
    {
      title: "Relationship Manager",
      description: "Track companies, external contacts, workspace staff, and project relationships without duplicated admin.",
      icon: Contact,
      color: "bg-[#234d6f]",
      targetView: "crm" as const,
    },
    {
      title: "People & HR",
      description: "Manage staff records, access, HR profiles, and operational information across the team.",
      icon: Users,
      color: "bg-[#597058]",
      targetView: "people" as const,
    },
    {
      title: "Documents & AI",
      description: "Analyse production documents, extract useful information, and connect it back to project data.",
      icon: ScanText,
      color: "bg-[#ff8600]",
      targetView: "dashboard" as const,
    },
    {
      title: "Calendar & Meetings",
      description: "Keep schedules, meetings, and project conversations connected to the work they support.",
      icon: Calendar,
      color: "bg-[#c03831]",
      targetView: "calendar" as const,
    },
    {
      title: "Onset",
      description: "Prepare for capture and onset workflows with tools designed to sit alongside bidding and production planning.",
      icon: Camera,
      color: "bg-[#3397da]",
      targetView: "onset" as const,
    },
  ].filter((section) => canViewPeopleHr || section.targetView !== "people");

  return (
    <div className="flex flex-col pb-12">
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 via-white to-slate-100 shadow-sm dark:border-slate-800 dark:from-slate-900 dark:via-slate-900 dark:to-slate-950">
        <div className="grid gap-8 px-6 py-8 lg:grid-cols-[1.05fr_0.95fr] lg:px-8">
          <div className="flex flex-col justify-center">
            <div className="mb-4 inline-flex w-fit items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 shadow-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400">
              <Sparkles className="h-3.5 w-3.5 text-[#ffcc01]" />
              Welcome to Project Tools
            </div>
            <h1 className="font-brandon text-4xl font-medium tracking-tight text-slate-950 dark:text-slate-100">
              Welcome to Project Tools, {firstName}
            </h1>
            <p className="mt-4 max-w-2xl text-sm leading-7 text-slate-600 dark:text-slate-300">
              This workspace brings together bidding, project setup, documents, people, relationships, and operations
              so your team can manage production in one connected system rather than across scattered tools.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <button
                onClick={onNewProject}
                className="inline-flex items-center gap-2 rounded-xl bg-[#ffcc01] px-4 py-2.5 text-sm font-medium text-slate-900 shadow-sm transition hover:-translate-y-0.5 hover:bg-[#e6b801]"
              >
                <Plus className="h-4 w-4" />
                Start a New Project
              </button>
              <div className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-600 shadow-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300">
                <Film className="h-4 w-4 text-[#ffcc01]" />
                Signed in as {displayName || "Team Member"}
              </div>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            {sectionHighlights.map((section) => {
              const Icon = section.icon;
              return (
                <button
                  key={section.title}
                  onClick={() => onNavigate(section.targetView)}
                  className="rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-sm outline-none transition hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus-visible:outline-none focus-visible:ring-0 dark:border-slate-800 dark:bg-slate-900"
                >
                  <div className="flex items-start gap-3">
                    <div className={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded text-white shadow-sm", section.color)}>
                      <Icon className="h-5.5 w-5.5" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">{section.title}</h3>
                      <p className="mt-1 text-xs leading-6 text-slate-500 dark:text-slate-400">{section.description}</p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
      <div className="px-3 pt-3 text-right text-[11px] font-medium text-slate-400 dark:text-slate-600">
        {appVersion}
      </div>
    </div>
  );
}
